import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import { Eye, FileUp, Mail } from "lucide-react";

import { DataTableNew } from "@/components/shared/DataTable";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import Select from "@/components/ui/Select";
import StatusBadge from "@/components/shared/StatusBadge";
import { Filter, FilterPill, FilterSelect } from "@/components/shared/Filter";
import Skeleton from "@/components/shared/Skeleton";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import {
  FEEDBACK_STATUS_COLOR as STATUS_COLOR,
  FEEDBACK_STATUS_LABEL as STATUS_LABEL,
  FEEDBACK_STATUS_OPTIONS,
  FEEDBACK_TYPE_LABEL as TYPE_LABEL,
  FEEDBACK_TYPE_OPTIONS,
} from "@/config/dropdownData";
import { GENERIC_ERROR } from "@/lib/errorMessages";
import { FeedbackService } from "@/services/feedbackService";
import { TenantService } from "@/services/tenantService";
import useLiveResource from "@/hooks/useLiveResource";
import FeedbackDateCell from "@/components/feedback/FeedbackDateCell";

import FeedbackDetailModal from "./components/FeedbackDetailModal";
import StatusChangeModal from "./components/StatusChangeModal";
import EmailSettingsModal from "./components/EmailSettingsModal";
import ImportFeedbackModal from "./components/ImportFeedbackModal";

// The row's current status, then where it may move next, as the server lists
// them in `next_statuses` — forward only. The page holds no copy of which move
// is allowed.
const statusOptionsFor = (row) => [row.status, ...(row?.next_statuses ?? [])].map((value) => ({ value, label: STATUS_LABEL[value] ?? value }));

// A Select in a table row: row height and a fixed width, so a row with
// dropdowns stands as tall as one with plain text, and the columns line up.
const CELL_SELECT = "h-8 w-[180px] py-1 text-[13px]";

// Fleet-wide product feedback. Rows come from GET /api/v2/platform/feedback
// (snake_case). Each names its tenant and never the person who sent it.
const FeedbackQueue = () => {
  const setHeader = useOutletContext();
  const tableRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [tenantFilter, setTenantFilter] = useState("");
  // Every tenant, suspended and closed included: their feedback is still in
  // the queue. A failed read leaves the filter empty; the queue still works.
  const [tenantOptions, setTenantOptions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [tableData, setTableData] = useState({ total: 0, isLoading: false });
  // Every status pick waits here for a confirm and a reason; nothing moves
  // until the server has saved it.
  const [pendingChange, setPendingChange] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [importing, setImporting] = useState(false);
  const [editingEmails, setEditingEmails] = useState(false);

  // `feedback.changed`: a tenant sent feedback, or another SuperAdmin moved
  // one. The table re-reads its current page.
  useLiveResource({ keys: "feedback", onChange: () => setReloadKey((k) => k + 1) });

  useEffect(() => {
    let live = true;
    TenantService.fetchTenantList({ pagination: false, includeDeleted: true })
      .then((res) => {
        if (!live) return;
        const options = (res?.data?.tenants ?? []).map((t) => ({ value: t.id, label: t.name || t.slug }));
        setTenantOptions(options.sort((a, b) => a.label.localeCompare(b.label)));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    setHeader({
      title: "Feedback",
      subTitle: tableData.isLoading ? <Skeleton className="h-5 w-28" /> : `Total ${tableData.total} feedback`,
    });
    return () => setHeader({ title: "", subTitle: "" });
  }, [tableData, setHeader]);

  const onStatusPick = (row, status) => {
    if (status === row.status) return;
    setPendingChange({ row, status });
  };

  // Mails nobody, so it saves on pick with no confirm. The PATCH answers with
  // the item under its new type — the statuses that type allows — and the
  // row takes all of it.
  const onTypePick = async (row, type) => {
    if (type === row.type) return;
    try {
      const res = await FeedbackService.updateType(row.id, type);
      onStatusSaved(res.data);
      toast.success(`Type changed to ${TYPE_LABEL[type] ?? type}.`);
    } catch (err) {
      toast.error(err?.userMessage || GENERIC_ERROR);
    }
  };

  // The PATCH answers with the item as it now stands — its new status and
  // the moves that status allows — so the row takes all of it.
  const onStatusSaved = (item) => {
    tableRef.current?.patchRow(item.id, item);
    setViewing((v) => (v?.id === item.id ? { ...v, ...item } : v));
    setPendingChange(null);
  };

  const COLUMNS = useMemo(
    () => [
      {
        key: "created_at",
        label: "Received",
        sortable: false,
        align: "left",
        render: (val, row) => <FeedbackDateCell date={val} imported={row.is_imported} />,
      },
      {
        key: "tenant_name",
        label: "Tenant",
        sortable: false,
        align: "left",
        render: (val) => <span className="font-medium text-[13px]">{val || "—"}</span>,
      },
      {
        key: "type",
        label: "Type",
        sortable: false,
        align: "left",
        // Only while the server says the type may change (`type_changeable`,
        // true at Open); text otherwise.
        render: (val, row) =>
          row.type_changeable ? (
            <Select
              aria-label={`Type of feedback from ${row.tenant_name ?? "tenant"}`}
              value={val}
              options={FEEDBACK_TYPE_OPTIONS}
              onChange={(next) => onTypePick(row, next)}
              className={CELL_SELECT}
            />
          ) : (
            <span className="text-[13px]">{TYPE_LABEL[val] ?? val}</span>
          ),
      },
      {
        key: "comment",
        label: "Comment",
        sortable: false,
        align: "left",
        render: (val) => (
          <span className="block max-w-[360px] overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-text-secondary" title={val}>
            {val}
          </span>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: false,
        align: "left",
        // A row with nowhere to move shows its status: an ended one its end,
        // Appreciation — never worked — none at all.
        render: (val, row) =>
          row.next_statuses?.length ? (
            <Select
              aria-label={`Status of feedback from ${row.tenant_name ?? "tenant"}`}
              value={val}
              options={statusOptionsFor(row)}
              onChange={(next) => onStatusPick(row, next)}
              className={CELL_SELECT}
            />
          ) : val ? (
            <StatusBadge text={STATUS_LABEL[val] ?? val} colorIndex={STATUS_COLOR[val] ?? 0} size="sm" />
          ) : (
            <span className="text-[12px] text-text-secondary">—</span>
          ),
      },
      {
        key: "id",
        label: "Action",
        sortable: false,
        align: "center",
        render: (_val, row) => (
          <Button variant="ghost" size="icon" aria-label="View feedback" title="View feedback" onClick={() => setViewing(row)} className="hover:bg-background-secondary">
            <Eye className="size-4" />
          </Button>
        ),
      },
    ],
    [],
  );

  const filters = [
    { key: "type", label: "All Types", value: typeFilter, onChange: setTypeFilter, options: FEEDBACK_TYPE_OPTIONS },
    { key: "status", label: "All Statuses", value: statusFilter, onChange: setStatusFilter, options: FEEDBACK_STATUS_OPTIONS },
  ];

  const activeFilters = [
    typeFilter && { key: "type", label: `Type: ${TYPE_LABEL[typeFilter]}`, clear: () => setTypeFilter("") },
    statusFilter && { key: "status", label: `Status: ${STATUS_LABEL[statusFilter]}`, clear: () => setStatusFilter("") },
  ].filter(Boolean);

  const extraParams = useMemo(
    () => ({ status: statusFilter || undefined, type: typeFilter || undefined, tenant_id: tenantFilter || undefined }),
    [statusFilter, typeFilter, tenantFilter],
  );

  return (
    <PermissionGuard permission={PERMISSIONS.SUPER_ADMIN_MANAGE}>
      <div className="flex flex-col gap-2.5" style={{ zoom: 0.85 }}>
        <div className="flex items-center justify-end gap-2.5">
          {/* Outside the Filters panel, where the scope is always in sight. */}
          <div className="mr-auto w-[240px]">
            <FilterSelect label="All Tenants" value={tenantFilter} onChange={setTenantFilter} options={tenantOptions} searchable fullWidth />
          </div>
          <Button variant="outline" onClick={() => setEditingEmails(true)}>
            <Mail className="size-4" />
            Email settings
          </Button>
          <Button variant="secondary" onClick={() => setImporting(true)}>
            <FileUp className="size-4" />
            Import feedback
          </Button>
          <Filter
            filters={filters}
            activeFilters={activeFilters}
            showFilters={showFilters}
            onToggle={() => setShowFilters((p) => !p)}
            onClearAll={() => {
              setStatusFilter("");
              setTypeFilter("");
            }}
            renderFilter={(f) => <FilterSelect key={f.key} {...f} />}
            renderActiveFilter={(f) => <FilterPill key={f.key} {...f} />}
          />
        </div>

        <Card className="p-0 bg-table-heading border-border-primary">
          <DataTableNew
            ref={tableRef}
            key="feedback-queue"
            className="rounded-none"
            columns={COLUMNS}
            api={FeedbackService.list}
            sortable={false}
            dataPath="feedback"
            extraParams={extraParams}
            onDataFetch={setTableData}
            reloadKey={reloadKey}
            paginationLabel="feedback"
          />
        </Card>
      </div>

      {viewing && (
        <FeedbackDetailModal
          feedback={viewing}
          typeLabel={TYPE_LABEL[viewing.type] ?? viewing.type}
          statusLabel={STATUS_LABEL[viewing.status] ?? viewing.status}
          onClose={() => setViewing(null)}
        />
      )}

      {editingEmails && <EmailSettingsModal onClose={() => setEditingEmails(false)} />}

      {importing && <ImportFeedbackModal onClose={() => setImporting(false)} onImported={() => setReloadKey((k) => k + 1)} />}

      {pendingChange && (
        <StatusChangeModal
          feedback={pendingChange.row}
          toStatus={pendingChange.status}
          fromLabel={STATUS_LABEL[pendingChange.row.status] ?? pendingChange.row.status}
          toLabel={STATUS_LABEL[pendingChange.status] ?? pendingChange.status}
          onClose={() => setPendingChange(null)}
          onSaved={onStatusSaved}
        />
      )}
    </PermissionGuard>
  );
};

export default FeedbackQueue;
